import { useState, useEffect } from "react";
import axios from "axios";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from "recharts";
import { DollarSign, TrendingDown, Trash2, Zap, AlertCircle } from "lucide-react";
import { useCluster } from "@/components/layout/Layout";
import { COST } from "@/constants/testIds";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#6366f1", "#22d3ee", "#34d399", "#fbbf24", "#f87171", "#a78bfa", "#fb923c", "#4ade80"];

function CostStat({ label, value, color, icon: Icon, sub }) {
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={13} className={color} />
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</span>
      </div>
      <p className={cn("text-2xl font-semibold tracking-tight", color)} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
        ${typeof value === "number" ? value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : value}
      </p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs">
      <p className="text-slate-400 mb-1">{payload[0].name}</p>
      <p className="text-slate-200 font-mono font-semibold">${payload[0].value?.toLocaleString()}</p>
      {payload[0].payload?.percent && <p className="text-slate-500">{payload[0].payload.percent}% of total</p>}
    </div>
  );
};

export default function CostIntelligence() {
  const { clusterId } = useCluster();
  const [cost, setCost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/clusters/${clusterId}/cost`)
      .then((r) => { setCost(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [clusterId]);

  if (loading || !cost) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => <div key={i} className="h-24 bg-slate-800/50 rounded-md animate-pulse" />)}
      </div>
    );
  }

  return (
    <div data-testid={COST.page} className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-50" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Cost Intelligence</h2>
        <div className="text-xs text-slate-500">Cluster: <span className="text-slate-300 font-mono">{clusterId}</span></div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <CostStat data-testid={COST.totalCost} label="Monthly Total" value={cost.total_monthly} color="text-slate-50" icon={DollarSign} sub="Estimated" testId={COST.totalCost} />
        <CostStat label="Potential Savings" value={cost.potential_savings} color="text-emerald-400" icon={TrendingDown} sub="Rightsizing" />
        <CostStat label="Idle Resources" value={cost.idle_cost} color="text-amber-400" icon={Trash2} sub="Unused cost" />
        <CostStat data-testid={COST.savingsWidget} label="Annual Estimate" value={Math.round(cost.total_monthly * 12)} color="text-indigo-400" icon={Zap} sub="Projected" />
      </div>

      {/* Namespace breakdown + Pie */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div data-testid={COST.namespaceBreakdown} className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
          <h3 className="text-sm font-semibold text-slate-200 mb-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Namespace Breakdown</h3>
          <div className="space-y-3">
            {cost.namespace_breakdown?.map((ns, i) => (
              <div key={ns.namespace} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-300 font-mono">{ns.namespace}</span>
                  <span className="text-slate-400 font-mono">${ns.cost.toLocaleString()}</span>
                </div>
                <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${ns.percent}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                </div>
                <p className="text-[10px] text-slate-600">{ns.percent}% · CPU ${ns.cpu_cost} · Memory ${ns.memory_cost}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Pie chart */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
          <h3 className="text-sm font-semibold text-slate-200 mb-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Cost Distribution</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={cost.namespace_breakdown} dataKey="cost" nameKey="namespace" cx="50%" cy="50%" outerRadius={90} innerRadius={55}>
                {cost.namespace_breakdown?.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="transparent" />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Cost trend */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
        <h3 className="text-sm font-semibold text-slate-200 mb-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Cost Trend (6 months)</h3>
        <ResponsiveContainer width="100%" height={140}>
          <LineChart data={cost.cost_trend} margin={{ top: 0, right: 0, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 9, fill: "#475569" }} />
            <YAxis tick={{ fontSize: 9, fill: "#475569" }} tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
            <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: "6px", fontSize: 11 }} />
            <Line type="monotone" dataKey="cost" stroke="#6366f1" strokeWidth={2} dot={{ fill: "#6366f1", r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Rightsizing recommendations */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-md overflow-hidden animate-fade-in">
        <div className="px-4 py-3 border-b border-slate-800 flex items-center gap-2">
          <AlertCircle size={13} className="text-amber-400" />
          <h3 className="text-sm font-semibold text-slate-200" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Rightsizing Recommendations</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-800">
                {["Deployment", "Namespace", "Monthly Cost", "Potential Saving", "Status"].map((h) => (
                  <th key={h} className="px-4 py-2.5 text-left font-semibold uppercase tracking-widest text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cost.deployment_breakdown?.map((dep) => (
                <tr key={dep.name} className="border-b border-slate-800/40 hover:bg-slate-800/20 transition-colors">
                  <td className="px-4 py-2.5 text-slate-200 font-mono">{dep.name}</td>
                  <td className="px-4 py-2.5 text-slate-400">{dep.namespace}</td>
                  <td className="px-4 py-2.5 text-slate-300 font-mono">${dep.cost.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-emerald-400 font-mono">-${dep.rightsizing_saving.toFixed(0)}</td>
                  <td className="px-4 py-2.5">
                    <span className={cn("text-[10px] px-1.5 py-0.5 rounded border",
                      dep.is_idle ? "text-rose-400 bg-rose-400/10 border-rose-400/20" : "text-emerald-400 bg-emerald-400/10 border-emerald-400/20"
                    )}>{dep.is_idle ? "Idle" : "Active"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
